/*#
# GitHub import

Point virta at a repo and it pulls in what it can: the project's description,
homepage, package name and version (from `package.json`), and every issue as
a task — title, body, labels as tags, open or closed, attributed to the
GitHub author and dated when it was opened. That is how a real repo is
onboarded, by UI or by the `import` verb.

Every event an import writes has a deterministic id (`gh:owner/repo#12:created`),
and the [sync log](/log/) is idempotent by id, so importing again — to pick up
new issues, or after a failed run — cannot duplicate anything. Once a repo is
imported, virta is the source of truth for its tasks; GitHub issues become a
place virta renders to.

**Writing back** (`pushToGitHub`, `virta project push`): an issue's reporter
waits on GitHub, so a task that came *from* GitHub carries its outcome back —
closed in virta, its issue is closed with the close reason as a comment
(a task that ended obsolete or rejected closes it as *not planned*); reopened, it is reopened. Only imported tasks (`gh:` ids) are
touched; an internal task never mirrors out. It reconciles against the
issues' live state, so running it again changes nothing, and a dry run
prints exactly what it would post.

The import uses GitHub's public API without a token (60 requests an hour per
address); pass one for private repos or bigger backlogs.
*/

/*{ "parent": "Reference", "order": 90 }*/

import type { EventDraft, Task } from './model'
import type { VirtaStore } from './store'
import { normalizeTag, tagValues } from './tags'

export interface RepoRef {
  owner: string
  repo: string
}

/** `owner/repo`, or any github.com URL, → parts. Null if it is neither. */
export function parseRepo(ref: string): RepoRef | null {
  const text = ref.trim()
  const url = text.match(
    /github\.com[/:]([^/\s]+)\/([^/\s#?]+?)(?:\.git)?(?:[/#?].*)?$/
  )
  if (url) return { owner: url[1]!, repo: url[2]! }
  const bare = text.match(/^([\w.-]+)\/([\w.-]+)$/)
  if (bare) return { owner: bare[1]!, repo: bare[2]! }
  return null
}

export interface RepoFacts {
  description: string
  homepage: string
  defaultBranch: string
  pushedAt: string
  package: string
  version: string
}

export interface GitHubIssue {
  number: number
  title: string
  body: string | null
  state: 'open' | 'closed'
  labels: ({ name: string } | string)[]
  user: { login: string } | null
  created_at: string
  closed_at: string | null
  pull_request?: unknown
}

export type FetchLike = (
  url: string,
  init?: { headers?: Record<string, string>; method?: string; body?: string }
) => Promise<{
  ok: boolean
  status: number
  json(): Promise<unknown>
  text(): Promise<string>
}>

export interface ImportOptions {
  /** the project name to tag tasks with; default: the repo name */
  project?: string
  /** a GitHub token, for private repos or more than 60 requests an hour */
  token?: string
  fetch?: FetchLike
  /** how many pages of 100 issues to read (default 5) */
  maxPages?: number
}

const API = 'https://api.github.com'

/** GitHub's refusal, said usefully: a private repo answers 404 to a request with no token (board #140). */
function githubRefusal(ref: RepoRef, status: number, token?: string): string {
  const where = `GitHub: ${ref.owner}/${ref.repo} → ${status}`
  return (status === 404 || status === 401) && !token
    ? `${where} (a private repo needs a token: set GH_TOKEN, or pass --token)`
    : where
}

function headers(token?: string): Record<string, string> {
  const h: Record<string, string> = { Accept: 'application/vnd.github+json' }
  if (token) h.Authorization = `Bearer ${token}`
  return h
}

/** The repo's own facts: description, homepage, and package.json's name and version. */
export async function fetchRepoFacts(
  ref: RepoRef,
  options: ImportOptions = {}
): Promise<RepoFacts> {
  const fetchFn = options.fetch ?? (globalThis.fetch as unknown as FetchLike)
  const res = await fetchFn(`${API}/repos/${ref.owner}/${ref.repo}`, {
    headers: headers(options.token),
  })
  if (!res.ok) throw new Error(githubRefusal(ref, res.status, options.token))
  const repo = (await res.json()) as {
    description?: string | null
    homepage?: string | null
    default_branch?: string
    pushed_at?: string
  }
  const facts: RepoFacts = {
    description: repo.description ?? '',
    homepage: repo.homepage ?? '',
    defaultBranch: repo.default_branch ?? 'main',
    pushedAt: repo.pushed_at ?? '',
    package: '',
    version: '',
  }
  try {
    const pkg = await fetchFn(
      `https://raw.githubusercontent.com/${ref.owner}/${ref.repo}/${facts.defaultBranch}/package.json`
    )
    if (pkg.ok) {
      const json = (await pkg.json()) as {
        name?: string
        version?: string
        private?: boolean
      }
      if (json.name && !json.private) facts.package = json.name
      if (json.version) facts.version = json.version
    }
  } catch {
    /* no package.json, or not a JS project — fine */
  }
  return facts
}

/** Every issue (not pull request), open and closed, newest pages first. */
export async function fetchIssues(
  ref: RepoRef,
  options: ImportOptions = {}
): Promise<GitHubIssue[]> {
  const fetchFn = options.fetch ?? (globalThis.fetch as unknown as FetchLike)
  const out: GitHubIssue[] = []
  for (let page = 1; page <= (options.maxPages ?? 5); page++) {
    const res = await fetchFn(
      `${API}/repos/${ref.owner}/${ref.repo}/issues?state=all&per_page=100&page=${page}`,
      { headers: headers(options.token) }
    )
    if (!res.ok) throw new Error(githubRefusal(ref, res.status, options.token))
    const batch = (await res.json()) as GitHubIssue[]
    for (const issue of batch) if (!issue.pull_request) out.push(issue)
    if (batch.length < 100) break
  }
  return out.sort((a, b) => a.number - b.number)
}

export interface PushAction {
  /** `owner/repo#n` */
  issue: string
  taskId: string
  num: number
  title: string
  action: 'close' | 'reopen'
  /** GitHub's `state_reason` for a close */
  stateReason?: 'completed' | 'not_planned'
  /** the comment posted with it */
  comment: string
}

export interface PushResult {
  /** imported tasks looked at */
  checked: number
  /** what differs from GitHub (applied unless `dryRun`) */
  actions: PushAction[]
  applied: number
  dryRun: boolean
  /** the GitHub repos it compared against (and wrote to, unless `dryRun`) */
  repos: string[]
}

export interface PushOptions {
  /** only this project's tasks: this or `all`, never neither (board #3213) */
  project?: string
  /** every project on the board */
  all?: boolean
  /** needed: writing to GitHub takes a token with issue access */
  token?: string
  fetch?: FetchLike
  /** say what would change, change nothing */
  dryRun?: boolean
}

/** How a closed task ended, as GitHub says it: not planned, or completed. */
const NOT_PLANNED = new Set([
  'superseded',
  'obsolete',
  'rejected',
  'not-a-task',
  'duplicate',
  'absorbed',
])

/**
 * Carry imported tasks' outcomes back to their issues (see the page above):
 * closed in virta → close the issue with the reason; reopened → reopen it.
 * Reconciles against the live issue state, so it is safe to repeat.
 */
export async function pushToGitHub(
  store: VirtaStore,
  options: PushOptions = {}
): Promise<PushResult> {
  const fetchFn = options.fetch ?? (globalThis.fetch as unknown as FetchLike)
  const dryRun = options.dryRun ?? false
  if (!options.token && !dryRun)
    throw new Error('writing to GitHub needs a token (GH_TOKEN, or --token)')
  // this writes outward, as the user: it never guesses whose issues
  if (!options.project && !options.all)
    throw new Error(
      'say which project to push (project), or all: true for every project on the board'
    )
  if (options.project && options.all)
    throw new Error('a project or all, not both')
  const scope = options.project ? ` project:${options.project}` : ''
  const byRepo = new Map<string, { ref: RepoRef; tasks: [number, Task][] }>()
  // archived:any — a task that ended obsolete or rejected is archived,
  // and its reporter still needs to hear (board #753)
  for (const task of store.query(`source:github is:any archived:any${scope}`)) {
    const m = /^gh:([^/]+)\/([^#]+)#(\d+)$/.exec(task.id)
    if (!m) continue
    const key = `${m[1]}/${m[2]}`
    let entry = byRepo.get(key)
    if (!entry)
      byRepo.set(
        key,
        (entry = { ref: { owner: m[1]!, repo: m[2]! }, tasks: [] })
      )
    entry.tasks.push([Number(m[3]), task])
  }
  const actions: PushAction[] = []
  let checked = 0
  for (const [key, { ref, tasks }] of byRepo) {
    // issue order, not the store's recency order (a tie there is a coin toss)
    tasks.sort((a, b) => a[0] - b[0])
    const state = new Map(
      (await fetchIssues(ref, options)).map((i) => [i.number, i.state])
    )
    for (const [number, task] of tasks) {
      checked++
      const live = state.get(number)
      if (!live) continue
      const closed = task.closedAt !== null
      if (closed === (live === 'closed')) continue
      const issue = `${key}#${number}`
      if (closed) {
        // how it ended: the finer word (closed:duplicate) if there is one,
        // else the ending status (obsolete / rejected / done)
        const how =
          tagValues(task.tags, 'closed')[0] ??
          (tagValues(task.tags, 'status')[0] === 'done'
            ? ''
            : tagValues(task.tags, 'status')[0] ?? '')
        const reason =
          [...store.events(task.id)].reverse().find((e) => e.kind === 'closed')
            ?.payload.reason ?? ''
        actions.push({
          issue,
          taskId: task.id,
          num: task.num,
          title: task.title,
          action: 'close',
          stateReason: NOT_PLANNED.has(how) ? 'not_planned' : 'completed',
          comment: `Closed in virta${how ? ` (${how})` : ''}${
            String(reason).trim() ? `: ${String(reason).trim()}` : '.'
          }`,
        })
      } else
        actions.push({
          issue,
          taskId: task.id,
          num: task.num,
          title: task.title,
          action: 'reopen',
          comment: 'Reopened in virta.',
        })
    }
  }
  let applied = 0
  if (!dryRun)
    for (const a of actions) {
      const [repoPath, number] = a.issue.split('#') as [string, string]
      const base = `${API}/repos/${repoPath}/issues/${number}`
      const h = {
        ...headers(options.token),
        'Content-Type': 'application/json',
      }
      const commented = await fetchFn(`${base}/comments`, {
        method: 'POST',
        headers: h,
        body: JSON.stringify({ body: a.comment }),
      })
      if (!commented.ok)
        throw new Error(`GitHub: comment on ${a.issue} → ${commented.status}`)
      const set = await fetchFn(base, {
        method: 'PATCH',
        headers: h,
        body: JSON.stringify(
          a.action === 'close'
            ? { state: 'closed', state_reason: a.stateReason }
            : { state: 'open' }
        ),
      })
      if (!set.ok)
        throw new Error(`GitHub: ${a.action} ${a.issue} → ${set.status}`)
      applied++
    }
  return { checked, actions, applied, dryRun, repos: [...byRepo.keys()] }
}

/** Label → tag: the GitHub defaults map to `kind:`, anything else is `label:<name>`. */
export function labelTag(label: string): string {
  const name = label.trim().toLowerCase()
  const kinds: Record<string, string> = {
    bug: 'kind:bug',
    enhancement: 'kind:feature',
    feature: 'kind:feature',
    documentation: 'kind:docs',
    docs: 'kind:docs',
    question: 'kind:question',
    chore: 'kind:chore',
  }
  return kinds[name] ?? normalizeTag(`label:${name.replace(/\s+/g, '-')}`)
}

/**
 * Issues → event drafts with deterministic ids, so a second import of the
 * same repo is a no-op for anything already in the log.
 */
export function issueDrafts(
  ref: RepoRef,
  project: string,
  issues: GitHubIssue[]
): EventDraft[] {
  const drafts: EventDraft[] = []
  for (const issue of issues) {
    if (issue.pull_request) continue
    const key = `gh:${ref.owner}/${ref.repo}#${issue.number}`
    const by = `github:${issue.user?.login ?? 'unknown'}`
    // an open issue is acknowledged work: backlog, not the inbox; a closed
    // one is done (closed is done — a closed task lives in the done lane)
    const tags = new Set<string>([
      `project:${project}`,
      'source:github',
      `github:${issue.number}`,
      issue.state === 'closed' ? 'status:done' : 'status:backlog',
    ])
    for (const label of issue.labels) {
      const name = typeof label === 'string' ? label : label.name
      if (name) tags.add(labelTag(name))
    }
    drafts.push({
      id: `${key}:created`,
      taskId: key,
      at: issue.created_at,
      by,
      kind: 'created',
      payload: {
        title: issue.title,
        body: issue.body ?? '',
        tags: Array.from(tags),
      },
    })
    if (issue.state === 'closed') {
      drafts.push({
        id: `${key}:closed`,
        taskId: key,
        at: issue.closed_at ?? issue.created_at,
        by,
        kind: 'closed',
        payload: { reason: '' },
      })
    }
  }
  return drafts
}

export interface ImportResult {
  project: string
  repo: string
  facts: RepoFacts
  issues: number
  /** tasks this import created (0 on a repeat import with nothing new) */
  created: number
  /** hand-filed tasks tagged github:<n> it took as their issue's (board #769) */
  adopted: number
  /** events it wrote: a task's creation, plus a close it lacked (board #180) */
  events: number
}

/**
 * Onboard a repo: settings from its facts, its issues as tasks. Safe to run
 * again — new issues arrive, everything else is already there.
 */
export async function importProject(
  store: VirtaStore,
  repoRef: string,
  options: ImportOptions = {}
): Promise<ImportResult> {
  const ref = parseRepo(repoRef)
  if (!ref) throw new Error(`not a GitHub repo: ${repoRef}`)
  const project = options.project ?? ref.repo
  const facts = await fetchRepoFacts(ref, options)
  await store.setProject(project, {
    repo: `${ref.owner}/${ref.repo}`,
    description: facts.description,
    site: facts.homepage,
    package: facts.package,
    version: facts.version,
  })
  const issues = await fetchIssues(ref, options)
  // Only what the store does not hold: an event id must always carry the
  // same body (a re-import after the importer changed what it writes would
  // otherwise replace and re-sequence it — tosijs-platform#25). A held task
  // gets only a `closed` it lacks.
  // A task someone filed by hand for an issue (tagged github:<n> in this
  // project, not imported) is that issue's task: adopt it rather than file
  // a second (board #769) — it becomes source:github, so github-push
  // carries its outcome back, and it takes a close it lacks
  const key = (n: number) => `gh:${ref.owner}/${ref.repo}#${n}`
  const handMade = new Map<string, Task>()
  for (const t of store.query(
    `project:${project} github is:any archived:any`,
    null
  ))
    for (const n of tagValues(t.tags, 'github'))
      if (
        /^\d+$/.test(n) &&
        t.id !== key(Number(n)) &&
        !store.get(key(Number(n)))
      )
        handMade.set(key(Number(n)), t)
  const drafts = issueDrafts(ref, project, issues).filter((d) => {
    if (handMade.has(d.taskId)) return false
    const held = store.get(d.taskId)
    if (!held) return true
    return d.kind === 'closed' && held.closedAt === null
  })
  await store.import(drafts)
  let adopted = 0
  let adoptedEvents = 0
  for (const issue of issues) {
    const task = handMade.get(key(issue.number))
    if (!task || issue.pull_request) continue
    adopted++
    adoptedEvents += (await store.tag(task.id, 'source:github')).length
    if (issue.state === 'closed' && task.closedAt === null)
      adoptedEvents += (await store.close(task.id, 'closed on GitHub')).length
  }
  return {
    project,
    repo: `${ref.owner}/${ref.repo}`,
    facts,
    issues: issues.length,
    created: drafts.filter((d) => d.kind === 'created').length,
    adopted,
    events: drafts.length + adoptedEvents,
  }
}

/** The latest published version of an npm package, or '' if it cannot be read. */
export async function fetchNpmVersion(
  pkg: string,
  fetchFn: FetchLike = globalThis.fetch as unknown as FetchLike
): Promise<string> {
  try {
    const res = await fetchFn(
      `https://registry.npmjs.org/${encodeURIComponent(pkg)}/latest`
    )
    if (!res.ok) return ''
    const json = (await res.json()) as { version?: string }
    return json.version ?? ''
  } catch {
    return ''
  }
}
